import {
  trunkReportService,
  type BackendFile,
} from "@/services/api/trunkReport";
import type React from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export interface Download {
  id: string;
  filename: string;
  status: "pending" | "processing" | "completed" | "failed";
  progress?: number;
  createdAt: Date;
  completedAt?: Date;
  downloadUrl?: string;
  type: "trunk-report" | "other-report";
  backendFileId?: number;
  size?: number;
  metadata?: {
    vehicleInfo?: string;
    requestTimestamp?: number;
    period?: string;
  };
}

interface DownloadsContextType {
  downloads: Download[];
  addDownload: (download: Omit<Download, "id" | "createdAt">) => string;
  updateDownload: (id: string, updates: Partial<Download>) => void;
  removeDownload: (id: string) => void;
  deleteBackendFile: (fileId: number) => Promise<boolean>;
  getRecentDownloads: (limit?: number) => Download[];
  pendingDownloadsCount: number;
  processingDownloadsCount: number;
  newCompletedDownloadsCount: number;
  markDownloadsAsViewed: () => void;
  startPolling: () => void;
  stopPolling: () => void;
  isPolling: boolean;
  syncWithBackend: (showNotifications?: boolean) => Promise<Download[]>;
}

const DownloadsContext = createContext<DownloadsContextType | undefined>(
  undefined,
);

export const useDownloads = () => {
  const context = useContext(DownloadsContext);
  if (!context) {
    throw new Error(
      "useDownloads must be used within a DownloadsProvider. " +
        "Make sure to wrap your app with <DownloadsProvider> at the root level.",
    );
  }
  return context;
};

export const DownloadsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [downloads, setDownloads] = useState<Download[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [previousBackendFiles, setPreviousBackendFiles] = useState<
    BackendFile[]
  >([]);
  const [viewedCompletedDownloads, setViewedCompletedDownloads] = useState<
    Set<string>
  >(new Set());
  const [notifiedCompletedFiles, setNotifiedCompletedFiles] = useState<
    Set<number>
  >(new Set());
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const savedDownloads = localStorage.getItem("app-downloads");
    const savedViewedDownloads = localStorage.getItem("app-viewed-downloads");

    if (savedDownloads) {
      try {
        const parsed: Download[] = JSON.parse(savedDownloads);
        setDownloads(
          parsed.map((d: Download): Download => ({
            ...d,
            createdAt: new Date(d.createdAt),
            completedAt: d.completedAt ? new Date(d.completedAt) : undefined,
          })),
        );
      } catch (error) {
        console.error("Error loading downloads from localStorage:", error);
      }
    }

    if (savedViewedDownloads) {
      try {
        const parsed: string[] = JSON.parse(savedViewedDownloads);
        setViewedCompletedDownloads(new Set(parsed));
      } catch (error) {
        console.error(
          "Error loading viewed downloads from localStorage:",
          error,
        );
      }
    }

    syncWithBackend();
  }, []);

  useEffect(() => {
    localStorage.setItem("app-downloads", JSON.stringify(downloads));
  }, [downloads]);

  useEffect(() => {
    localStorage.setItem(
      "app-viewed-downloads",
      JSON.stringify(Array.from(viewedCompletedDownloads)),
    );
  }, [viewedCompletedDownloads]);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const convertBackendFileToDownload = (backendFile: BackendFile): Download => {
    const status: Download["status"] =
      backendFile.status === 1
        ? "completed"
        : backendFile.status === 0
          ? "processing"
          : "pending";

    return {
      id: `backend-${backendFile.id}`,
      filename: backendFile.original_name || backendFile.name,
      status,
      createdAt: new Date(backendFile.created_at),
      completedAt:
        status === "completed" ? new Date(backendFile.updated_at) : undefined,
      downloadUrl:
        status === "completed"
          ? `/api/downloads/${backendFile.id}/file`
          : undefined,
      type: "trunk-report",
      backendFileId: backendFile.id,
      size: backendFile.size,
      metadata: {
        vehicleInfo: "Relatório gerado",
        period: new Date(backendFile.created_at).toLocaleDateString("pt-BR"),
      },
    };
  };

  const findMatchingLocalDownload = (
    localDownloads: Download[],
    backendFile: BackendFile,
  ): Download | undefined => {
    return localDownloads.find((d) => {
      if (d.backendFileId) return false;

      const isTrunkReport =
        d.type === "trunk-report" && d.filename.includes("Relatório Trava Baú");

      if (!isTrunkReport) return false;

      const backendCreatedAt = new Date(backendFile.created_at).getTime();
      const localCreatedAt = d.createdAt.getTime();

      const timeDiff = backendCreatedAt - localCreatedAt;
      const isWithinTimeWindow = timeDiff >= -30000 && timeDiff <= 120000;

      if (d.metadata?.requestTimestamp) {
        const requestTime = d.metadata.requestTimestamp;
        const requestTimeDiff = Math.abs(backendCreatedAt - requestTime);
        return requestTimeDiff < 180000;
      }

      return isWithinTimeWindow;
    });
  };

  const syncWithBackend = async (
    showNotifications = false,
  ): Promise<Download[]> => {
    try {
      const response = await trunkReportService.downloadIndex({
        page: 1,
        per_page: 10,
      });
      const currentBackendFiles = response.data;

      if (showNotifications) {
        currentBackendFiles.forEach((currentFile: BackendFile) => {
          const previousFile = previousBackendFiles.find(
            (f) => f.id === currentFile.id,
          );

          if (
            (!previousFile || previousFile.status !== 1) &&
            currentFile.status === 1 &&
            !notifiedCompletedFiles.has(currentFile.id)
          ) {
            toast.success(
              `Novo download concluído: ${currentFile.original_name || currentFile.name}`,
              {
                duration: 5000,
              },
            );
            setNotifiedCompletedFiles((prev) =>
              new Set(prev).add(currentFile.id),
            );
          }
        });
      }

      setPreviousBackendFiles(currentBackendFiles);
      const backendDownloads = currentBackendFiles.map(
        convertBackendFileToDownload,
      );

      setDownloads((prev) => {
        const updatedDownloads: Download[] = [];
        const processedBackendIds = new Set<number>();
        const processedLocalIds = new Set<string>();

        prev.forEach((download) => {
          if (download.backendFileId) {
            const backendFile = currentBackendFiles.find(
              (bf) => bf.id === download.backendFileId,
            );
            if (backendFile) {
              const backendDownload = convertBackendFileToDownload(backendFile);
              updatedDownloads.push({
                ...backendDownload,
                id: download.id,
              });
              processedBackendIds.add(backendFile.id);
              processedLocalIds.add(download.id);
            }
          }
        });

        const remainingLocalDownloads = prev.filter(
          (d) => !d.backendFileId && !processedLocalIds.has(d.id),
        );

        const remainingBackendFiles = currentBackendFiles.filter(
          (bf) => !processedBackendIds.has(bf.id),
        );

        remainingBackendFiles.forEach((backendFile) => {
          const matchingLocal = findMatchingLocalDownload(
            remainingLocalDownloads,
            backendFile,
          );

          if (matchingLocal) {
            const backendDownload = convertBackendFileToDownload(backendFile);
            updatedDownloads.push({
              ...backendDownload,
              id: matchingLocal.id,
              metadata: {
                ...backendDownload.metadata,
                ...matchingLocal.metadata,
              },
            });

            processedBackendIds.add(backendFile.id);
            processedLocalIds.add(matchingLocal.id);

            console.log("Download local matched with backend:", {
              localId: matchingLocal.id,
              backendId: backendFile.id,
              localFilename: matchingLocal.filename,
              backendFilename: backendFile.original_name || backendFile.name,
            });
          } else {
            updatedDownloads.push(convertBackendFileToDownload(backendFile));
            processedBackendIds.add(backendFile.id);
          }
        });

        remainingLocalDownloads.forEach((download) => {
          if (!processedLocalIds.has(download.id)) {
            const now = new Date().getTime();
            const age = now - download.createdAt.getTime();

            if (
              (download.status === "pending" ||
                download.status === "processing") &&
              age > 300000
            ) {
              console.log("Removing old pending download:", download.filename);
              return;
            }

            updatedDownloads.push(download);
          }
        });

        return updatedDownloads.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
      });

      return backendDownloads;
    } catch (error) {
      console.error("Error syncing with backend:", error);
      return [];
    }
  };

  const deleteBackendFile = async (fileId: number): Promise<boolean> => {
    try {
      await trunkReportService.deleteDownload(fileId);
      setDownloads((prev) =>
        prev.filter((download) => download.backendFileId !== fileId),
      );
      setViewedCompletedDownloads((prev) => {
        const newSet = new Set(prev);
        newSet.delete(`backend-${fileId}`);
        return newSet;
      });
      setNotifiedCompletedFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });
      return true;
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error("Erro ao remover arquivo");
      return false;
    }
  };

  const startPolling = (): void => {
    if (isPolling) return;

    setIsPolling(true);
    console.log("Starting download polling...");

    pollingIntervalRef.current = setInterval(async () => {
      try {
        console.log("Polling downloads...");

        const response = await trunkReportService.downloadIndex({
          page: 1,
          per_page: 10,
        });
        const currentBackendFiles = response.data;

        currentBackendFiles.forEach((currentFile: BackendFile) => {
          const previousFile = previousBackendFiles.find(
            (f) => f.id === currentFile.id,
          );

          if (
            (!previousFile || previousFile.status !== 1) &&
            currentFile.status === 1 &&
            !notifiedCompletedFiles.has(currentFile.id)
          ) {
            toast.success(
              `Novo download concluído: ${currentFile.original_name || currentFile.name}`,
              {
                duration: 5000,
              },
            );
            setNotifiedCompletedFiles((prev) =>
              new Set(prev).add(currentFile.id),
            );
          }
        });

        setPreviousBackendFiles(currentBackendFiles);

        setDownloads((prev) => {
          const updatedDownloads: Download[] = [];
          const processedBackendIds = new Set<number>();
          const processedLocalIds = new Set<string>();

          prev.forEach((download) => {
            if (download.backendFileId) {
              const backendFile = currentBackendFiles.find(
                (bf) => bf.id === download.backendFileId,
              );
              if (backendFile) {
                const backendDownload =
                  convertBackendFileToDownload(backendFile);
                updatedDownloads.push({
                  ...backendDownload,
                  id: download.id,
                });
                processedBackendIds.add(backendFile.id);
                processedLocalIds.add(download.id);
              }
            }
          });

          const remainingLocalDownloads = prev.filter(
            (d) => !d.backendFileId && !processedLocalIds.has(d.id),
          );

          const remainingBackendFiles = currentBackendFiles.filter(
            (bf) => !processedBackendIds.has(bf.id),
          );

          remainingBackendFiles.forEach((backendFile) => {
            const matchingLocal = findMatchingLocalDownload(
              remainingLocalDownloads,
              backendFile,
            );

            if (matchingLocal) {
              const backendDownload = convertBackendFileToDownload(backendFile);
              updatedDownloads.push({
                ...backendDownload,
                id: matchingLocal.id,
                metadata: {
                  ...backendDownload.metadata,
                  ...matchingLocal.metadata,
                },
              });

              processedBackendIds.add(backendFile.id);
              processedLocalIds.add(matchingLocal.id);
            } else {
              updatedDownloads.push(convertBackendFileToDownload(backendFile));
              processedBackendIds.add(backendFile.id);
            }
          });

          remainingLocalDownloads.forEach((download) => {
            if (!processedLocalIds.has(download.id)) {
              const now = new Date().getTime();
              const age = now - download.createdAt.getTime();

              if (!(
                (download.status === "pending" ||
                  download.status === "processing") &&
                age > 300000
              )) {
                updatedDownloads.push(download);
              }
            }
          });

          return updatedDownloads.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );
        });

        const pendingBackendFiles = currentBackendFiles.filter(
          (file: BackendFile) => file.status !== 1,
        );

        console.log("Polling status:", {
          totalFiles: currentBackendFiles.length,
          pendingFiles: pendingBackendFiles.length,
        });

        if (pendingBackendFiles.length === 0) {
          stopPolling();
        }
      } catch (error) {
        console.error("Error during polling:", error);
      }
    }, 10000);
  };

  const stopPolling = (): void => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
  };

  const markDownloadsAsViewed = (): void => {
    const completedDownloadIds = downloads
      .filter((d) => d.status === "completed")
      .map((d) => d.id);

    setViewedCompletedDownloads((prev) => {
      const newSet = new Set(prev);
      completedDownloadIds.forEach((id) => newSet.add(id));
      return newSet;
    });
  };

  const addDownload = (
    downloadData: Omit<Download, "id" | "createdAt">,
  ): string => {
    const id = `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newDownload: Download = {
      ...downloadData,
      id,
      createdAt: new Date(),
    };

    setDownloads((prev) => [newDownload, ...prev]);

    toast.info(`Download iniciado: ${downloadData.filename}`, {
      duration: 3000,
    });

    return id;
  };

  const updateDownload = (id: string, updates: Partial<Download>): void => {
    setDownloads((prev) =>
      prev.map((download) => {
        if (download.id === id) {
          const updated = { ...download, ...updates };
          if (
            updates.status === "completed" &&
            download.status !== "completed"
          ) {
            toast.success(`Download concluído: ${download.filename}`, {
              duration: 5000,
            });
          } else if (
            updates.status === "failed" &&
            download.status !== "failed"
          ) {
            toast.error(`Falha no download: ${download.filename}`, {
              duration: 5000,
            });
          }
          return updated;
        }
        return download;
      }),
    );
  };

  const removeDownload = (id: string): void => {
    setDownloads((prev) => prev.filter((download) => download.id !== id));
    setViewedCompletedDownloads((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  const getRecentDownloads = (limit = 5): Download[] => {
    return downloads
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      .slice(0, limit);
  };

  const pendingDownloadsCount = downloads.filter(
    (d) => d.status === "pending" || d.status === "processing",
  ).length;

  const processingDownloadsCount = downloads.filter(
    (d) => d.status === "processing" || d.status === "pending",
  ).length;

  const newCompletedDownloadsCount = downloads.filter(
    (d) => d.status === "completed" && !viewedCompletedDownloads.has(d.id),
  ).length;

  return (
    <DownloadsContext.Provider
      value={{
        downloads,
        addDownload,
        updateDownload,
        removeDownload,
        deleteBackendFile,
        getRecentDownloads,
        pendingDownloadsCount,
        processingDownloadsCount,
        newCompletedDownloadsCount,
        markDownloadsAsViewed,
        startPolling,
        stopPolling,
        isPolling,
        syncWithBackend,
      }}
    >
      {children}
    </DownloadsContext.Provider>
  );
};
